import { COLOR, GROUP } from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { modifyInheritedValue } from 'ContentComponents/components/cmv2/utils/inheritance';
export const scanFieldTree = fields => {
  const fieldsToVisit = [...fields];
  let hasCollection = false;
  let hasGlobalStylesBindings = false;
  while (fieldsToVisit.length > 0 && !(hasCollection && hasGlobalStylesBindings)) {
    var _field$binding;
    const field = fieldsToVisit.pop();
    if (field.collection != null) hasCollection = true;
    if (((_field$binding = field.binding) === null || _field$binding === void 0 ? void 0 : _field$binding.context) === 'global_styles') hasGlobalStylesBindings = true;
    if (field.type === GROUP && field.children) {
      fieldsToVisit.push(...field.children);
    }
  }
  return {
    hasCollection,
    hasGlobalStylesBindings
  };
};
export const resolveFieldBindingProps = (field, fieldKey, context) => {
  const {
    binding
  } = field;
  if (!binding || !context) return {};
  const {
    bindings,
    collectionMap,
    isGlobalStylesLoading,
    onBindingChange
  } = context;
  if (binding.context === 'global_styles' && isGlobalStylesLoading) {
    return {
      isBindingLoading: true
    };
  }
  const fieldPath = fieldKey.join('.');
  const collectionData = collectionMap[binding.collection];

  // TEMP: The BE expects a theme. prefix for binding to theme values
  // This is just to get previews of bound values working while we figure this out
  let resolvedCollectionData;
  if (collectionData) {
    let {
      entries
    } = collectionData;
    if (entries && binding.context === 'global_styles') {
      entries = Object.keys(entries).reduce((newEntries, _fieldPath) => {
        newEntries[`theme.${_fieldPath}`] = collectionData.entries[_fieldPath];
        return newEntries;
      }, {});
    }
    resolvedCollectionData = Object.assign({}, collectionData, {
      entries
    });
  }
  return {
    collectionData: resolvedCollectionData,
    currentBinding: bindings[fieldPath],
    onBindingChange: ref => onBindingChange(fieldPath, ref)
  };
};

/**
 * Maps each bindable field type to the properties on its value object that hold collectible values.
 * Each entry is a string array naming the sub-properties to extract (e.g. ['color'] → fieldValue.color).
 *
 * An entry is required for any field type that should participate in collections.
 * Field types without an entry are skipped entirely during collection map building.
 */
const COLLECTION_VALUE_PROP_BY_FIELD_TYPE = {
  [COLOR]: ['color']
};

/**
 * Traverses a field tree and builds a map of collection IDs to property path → resolved value.
 * Used to generate the runtime lookup table for binding resolution.
 *
 * @param fields - The field spec to traverse
 * @param overrides - Current user-set values (mirrors module_body shape)
 * @param pathPrefix - Dot-path segments accumulated from parent group fields
 * @returns A CollectionMap keyed by collection ID, then by dotted property path to resolved value
 *
 * @example
 * const map = buildCollectionMap(fields, moduleOverrides);
 * // { theme_colors: { "palette.primary_accent.color": "#FF5733" } }
 */
export const buildCollectionMap = (fields, overrides = {}, pathPrefix = []) => {
  return fields.reduce((collectionMap, field) => {
    const propertyKey = COLLECTION_VALUE_PROP_BY_FIELD_TYPE[field.type];
    // Skip non-group fields with no collection or an unsupported field type
    if (field.type !== GROUP && (!field.collection || propertyKey === undefined)) {
      return collectionMap;
    }

    // Resolve the effective value
    let value = overrides[field.name];
    if (value === undefined) {
      value = field.default;
    } else if (field.default && !Array.isArray(field.default) &&
    // fields with array defaults (e.g. occurrence groups, multi-choice) — not supported
    typeof field.default === 'object' && typeof value === 'object') {
      // Merge partial overrides
      value = Object.assign({}, field.default, value);
    }
    if (field.type === GROUP) {
      if (field.children) {
        const childCollectionMap = buildCollectionMap(field.children, value || {}, [...pathPrefix, field.name]);
        // Merge child collection entries into the parent map
        for (const collectionId of Object.keys(childCollectionMap)) {
          var _existing$entries, _existing$renderShade;
          const existing = collectionMap[collectionId];
          const child = childCollectionMap[collectionId];
          collectionMap[collectionId] = {
            entries: Object.assign({}, (_existing$entries = existing === null || existing === void 0 ? void 0 : existing.entries) !== null && _existing$entries !== void 0 ? _existing$entries : {}, child.entries),
            renderShades: ((_existing$renderShade = existing === null || existing === void 0 ? void 0 : existing.renderShades) !== null && _existing$renderShade !== void 0 ? _existing$renderShade : false) || child.renderShades
          };
        }
      }
      return collectionMap;
    }
    // Add entry to collection map
    const collectionId = field.collection;
    for (const propKey of propertyKey) {
      var _value;
      const propValue = (_value = value) === null || _value === void 0 ? void 0 : _value[propKey];
      if (propValue != null && field.type === COLOR) {
        if (!collectionMap[collectionId]) {
          collectionMap[collectionId] = {
            entries: {},
            renderShades: false
          };
        }
        if (field.render_shades) {
          collectionMap[collectionId].renderShades = true;
        }
        collectionMap[collectionId].entries[[...pathPrefix, field.name, propKey].join('.')] = propValue;
      }
    }
    return collectionMap;
  }, {});
};

// TODO: The existing inherited_value resolver in inheritance.ts resolves property_value_paths by
// walking module data via getDeepValue. This resolver uses the CollectionMap instead (O(1) lookup).
// Consider whether the two can share logic once the binding feature matures.

/**
 * Resolves all binding paths in a binding reference against a specific collection's entries.
 *
 * The caller is responsible for selecting the correct collection from the CollectionMap
 * using the collection ID from field.binding.collection before calling this function.
 *
 * @param bindingRef - An InheritedValue whose property_value_paths point into the collection
 * @param collectionEntries - The entries for the specific collection: collectionMap[field.binding.collection].entries
 * @returns A map of prop key → resolved value for each path that resolves; empty if nothing resolves
 *
 * @example
 * const resolved = resolveBindingReference(
 *   { property_value_paths: { color: "palette.accent.color" } },
 *   collectionMap[field.binding.collection].entries
 * );
 * // { color: "#FF5733" }
 */
export const resolveBindingReference = (bindingRef, collectionEntries) => {
  const {
    property_value_paths: propertyValuePaths,
    modifier
  } = bindingRef;
  const {
    modifier_type,
    modifier_value
  } = modifier !== null && modifier !== void 0 ? modifier : {};
  // TODO: also support default_value_path as a single direct path into the collection entries,
  // similar to how inheritance.ts uses it for full-value references (useful for scalar collection fields)

  if (!propertyValuePaths) return {};
  const result = {};
  for (const [propKey, sourcePath] of Object.entries(propertyValuePaths)) {
    const resolved = collectionEntries[sourcePath];
    if (resolved == null) continue;
    result[propKey] = modifier_type && modifier_value != null ? modifyInheritedValue(resolved, modifier_type, modifier_value) : resolved;
  }
  return result;
};